import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AnalystNarrowingDimension, AnalystNarrowingOptions, NarrowingDimensionKind, NarrowingEntityKind } from 'src/app/models/analyst-output.model';
import { TranslateService } from 'src/app/services/translate.service';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';

/**
 * Display label per dimension, kept FE-side and never taken from the BE payload — the four
 * dimensions are a closed, code-controlled set, not free-form model output, so the label is a
 * static i18n key like any other piece of UI chrome rather than dynamic content that has to
 * travel untranslated (the way AnalystClarification's model-composed option text does).
 */
const DIMENSION_LABELS: Record<NarrowingDimensionKind, string> = {
  [NarrowingDimensionKind.Site]: 'Site',
  [NarrowingDimensionKind.Seg]: 'SEG',
  [NarrowingDimensionKind.Agent]: 'Agent',
  [NarrowingDimensionKind.DateRange]: 'Date range',
};

/**
 * The chat message a click sends, kept FE-side for the same reason as `DIMENSION_LABELS` — a
 * closed, code-controlled set rather than BE-sent text — so it can be translated before it is
 * emitted as the next turn's prompt instead of injecting untranslated English into the thread.
 */
const DIMENSION_PROMPTS: Record<NarrowingDimensionKind, string> = {
  [NarrowingDimensionKind.Site]: 'Narrow this to a specific site.',
  [NarrowingDimensionKind.Seg]: 'Narrow this to a specific SEG (similar exposure group).',
  [NarrowingDimensionKind.Agent]: 'Narrow this to a specific agent.',
  [NarrowingDimensionKind.DateRange]: 'Narrow this to a shorter date range.',
};

/**
 * A whole sentence per entity kind rather than a shared sentence with the noun interpolated into
 * it. Composing one sentence out of separately translated fragments only works in languages that
 * happen to share English word order and leave the noun uninflected; a translator given the whole
 * string can decline the noun, agree it with the count, and reorder the clause freely.
 */
const REASON_BY_ENTITY: Record<NarrowingEntityKind, string> = {
  [NarrowingEntityKind.Agents]: '{{count}} agents matched. That is too many to review individually.',
  [NarrowingEntityKind.LabResults]: '{{count}} lab results matched. That is too many to review individually.',
  [NarrowingEntityKind.RiskAssessments]: '{{count}} risk assessments matched. That is too many to review individually.',
  [NarrowingEntityKind.Segs]: '{{count}} SEGs matched. That is too many to review individually.',
};

@Component({
  standalone: false,
  selector: 'app-analyst-narrowing-options',
  templateUrl: './analyst-narrowing-options.component.html',
  styleUrls: ['./analyst-narrowing-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalystNarrowingOptionsComponent implements OnChanges {
  @Input() narrowingOptions: AnalystNarrowingOptions | null = null;
  @Input() disabled = false;
  @Input() answered = false;
  @Input() answeredText: string | null = null;
  @Output() optionSelected = new EventEmitter<string>();

  readonly trackByIndex = trackByIndex;

  dismissed = false;
  currentReasonKey = '';
  dimensionViews: { dimension: AnalystNarrowingDimension; labelKey: string }[] = [];

  constructor(private readonly translateService: TranslateService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['narrowingOptions']) {
      this.dismissed = false;
      this.currentReasonKey = this.reasonKey(this.narrowingOptions?.entityKind);
      this.dimensionViews = (this.narrowingOptions?.dimensions ?? []).map((dimension) => ({
        dimension,
        labelKey: this.dimensionLabel(dimension.kind),
      }));
    }
  }

  dimensionLabel(kind: NarrowingDimensionKind): string {
    return DIMENSION_LABELS[kind];
  }

  reasonKey(kind: NarrowingEntityKind | undefined): string {
    return kind !== undefined ? REASON_BY_ENTITY[kind] : '';
  }

  onSelect(dimension: AnalystNarrowingDimension): void {
    if (this.disabled) {
      return;
    }
    this.optionSelected.emit(this.translateService.getDynamicTranslation(DIMENSION_PROMPTS[dimension.kind]));
  }

  onClose(): void {
    this.dismissed = true;
  }
}
